import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "@tanstack/react-router"
import { Loader2, KeyRound, ArrowLeft, ShieldCheck, Lock, Truck, Activity } from "lucide-react"
import { z } from "zod"
import { useState, useEffect } from "react"
import { useResetPassword } from "@/hooks/auth/useForgotPassword"
import { Button } from "@/components/ui/button"
import { useDevice } from "@/hooks/useIsMobile"

import { emailSchema } from "@/lib/schemas/auth.schema"

const resetPasswordSchema = z.object({
  email: emailSchema,
  otp: z.string().min(6, "OTP must be 6 digits").max(6, "OTP must be 6 digits"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
})

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>

import { AuthLeftPanel } from "./AuthLeftPanel"

export function ResetPasswordForm() {
  const { isDesktop } = useDevice()
  const navigate = useNavigate()
  const resetPassMutation = useResetPassword()
  const [showPassword, setShowPassword] = useState(false)

  // Retrieve stored email
  const storedEmail = typeof window !== "undefined" ? localStorage.getItem("reset_email") || "" : ""

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
    defaultValues: { email: storedEmail, otp: "", newPassword: "" },
  })

  // Keep state sync in case localstorage loads late
  useEffect(() => {
    if (storedEmail) {
      setValue("email", storedEmail)
    }
  }, [storedEmail, setValue])

  const onSubmit = (values: ResetPasswordFormValues) => {
    resetPassMutation.mutate(values, {
      onSuccess: () => {
        // Clean up stored email
        localStorage.removeItem("reset_email")
        // Redirect to Login Page
        navigate({ to: "/login" })
      },
    })
  }

  return (
    <div className="min-h-[100dvh] lg:min-h-screen flex flex-col lg:flex-row w-full bg-surface-container-low">
      
      {/* Left Panel: Branding & Benefits */}
      {isDesktop && <AuthLeftPanel />}

      {/* Right Panel: Reset Password Form */}
      <main className="w-full lg:w-1/2 bg-surface-container-low flex flex-col h-[100dvh] lg:h-screen p-4 sm:p-6 overflow-y-auto">
        {/* Top-Left Back Navigation (Positioned at the top of the page on Mobile & Tablet, Hidden on Desktop) */}
        <div className="w-full lg:hidden shrink-0">
          <Link
            to="/forgot-password"
            className="group inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-primary transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-md p-1"
          >
            <ArrowLeft className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Back</span>
          </Link>
        </div>

        {/* Card Container (Centered with space between top navigation and card) */}
        <div className="w-full max-w-[380px] mx-auto my-auto py-6 pb-20 sm:pb-8 lg:py-0">
          <div className="bg-white w-full rounded-xl p-6 border-[0.5px] border-outline-variant flex flex-col gap-4.5 shadow-sm">
          <header className="flex flex-col gap-1.5">
            {/* <Link
              to="/forgot-password"
              className="hidden lg:inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 hover:text-primary transition-colors w-fit"
            >
              <ArrowLeft className="size-3" /> Back
            </Link> */}
            <h1 className="font-sans text-xl font-black text-on-surface">Reset Password</h1>
            <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
              Provide the OTP sent to your email and specify your new password.
            </p>
          </header>

          {/* Error Message */}
          {resetPassMutation.isError && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive font-medium">
              {(resetPassMutation.error as any)?.response?.data?.message || resetPassMutation.error.message || "Failed to reset password"}
            </p>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            
            {/* Email (Prefilled, ReadOnly but shown for validation/correction context) */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300" htmlFor="reset-email">
                Email address
              </label>
              <input
                id="reset-email"
                type="email"
                placeholder="name@company.com"
                aria-invalid={!!errors.email}
                className="w-full h-9 px-3 border-[0.5px] border-outline-variant rounded-lg text-xs bg-slate-50 text-slate-500 outline-none"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-[11px] text-primary font-medium">{errors.email.message}</p>
              )}
            </div>

            {/* OTP Code (6 split boxes) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                Verification Code (OTP)
              </label>
              <div className="flex justify-between gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    className="w-10 h-10 border border-slate-200 rounded-lg text-center font-bold text-sm bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all outline-none"
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "")
                      e.target.value = val

                      // Set form value for OTP hook
                      const currentOtp = [0, 1, 2, 3, 4, 5]
                        .map((i) => (document.getElementById(`otp-${i}`) as HTMLInputElement)?.value || "")
                        .join("")
                      setValue("otp", currentOtp, { shouldValidate: true })

                      if (val && index < 5) {
                        const nextInput = document.getElementById(`otp-${index + 1}`)
                        if (nextInput) (nextInput as HTMLInputElement).focus()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !(e.currentTarget as HTMLInputElement).value && index > 0) {
                        const prevInput = document.getElementById(`otp-${index - 1}`)
                        if (prevInput) {
                          (prevInput as HTMLInputElement).focus()
                        }
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault()
                      const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6)
                      pastedData.split("").forEach((char, idx) => {
                        const targetInput = document.getElementById(`otp-${idx}`) as HTMLInputElement
                        if (targetInput) {
                          targetInput.value = char
                        }
                      })
                      setValue("otp", pastedData, { shouldValidate: true })
                      const lastFocused = document.getElementById(`otp-${Math.min(pastedData.length, 5)}`)
                      if (lastFocused) lastFocused.focus()
                    }}
                  />
                ))}
              </div>
              {/* Hidden input to keep react-hook-form schema compatibility */}
              <input type="hidden" {...register("otp")} />
              {errors.otp && (
                <p className="text-[11px] text-primary font-medium">{errors.otp.message}</p>
              )}
            </div>

            {/* New Password */}
            <div className="flex flex-col gap-1 relative">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300" htmlFor="reset-password">
                New Password
              </label>
              <div className="relative">
                <input
                  id="reset-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  aria-invalid={!!errors.newPassword}
                  className="w-full h-9 px-3 pr-12 border-[0.5px] border-outline-variant rounded-lg text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0"
                  {...register("newPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-primary uppercase tracking-wider hover:underline focus:outline-none cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-[11px] text-primary font-medium">{errors.newPassword.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={resetPassMutation.isPending}
              className="w-full h-9 bg-primary-container text-on-primary-container font-semibold rounded-lg text-xs transition-all duration-200 active:scale-[0.98] flex items-center justify-center hover:bg-primary-container/95 cursor-pointer mt-1"
            >
              {resetPassMutation.isPending ? (
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              ) : (
                <KeyRound className="size-3.5 mr-1.5" />
              )}
              {resetPassMutation.isPending ? "Resetting…" : "Reset Password"}
            </Button>
          </form>
        </div>
      </div>
    </main>

    </div>
  )
}
